// src/components/learning/ListCourse.js
import React, { useEffect, useState } from "react";
import { FaChevronDown, FaChevronUp, FaCheckCircle } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { CourseCard } from "./components/CourseCard";
import { baseAxios } from "../../apis";
import DOMPurify from "dompurify";
import SkeletonCard from "../skeletons/SkeletonCard";

// Komponen untuk satu item kursus (accordion)
const CourseItemSkeleton = () => {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header Accordion */}
      <div
        className={`flex justify-between items-center p-5 cursor-pointer bg-white`}
      >
        <div className="flex items-start flex-col">
          {/* <div className="flex items-center bg-purple-100 text-purple-700 text-xs font-bold mr-4 px-3 py-1 rounded-md mb-2">
            <FaCheckCircle className="mr-2" />
            <SkeletonCard
              type="text"
              lines={1}
              widths={["50px"]}
              heights={["10px"]}
            />
          </div> */}
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              <SkeletonCard
                type="text"
                lines={1}
                widths={["100px"]}
                heights={["10px"]}
              />
            </h3>
            <p className="text-sm text-gray-500">
              <SkeletonCard
                type="text"
                lines={1}
                widths={["50px"]}
                heights={["10px"]}
              />
            </p>
          </div>
        </div>
        <FaChevronDown />
      </div>

      {/* Body Accordion (Collapsible) */}
    </div>
  );
};

// Komponen untuk satu item kursus (accordion)
const CourseItem = ({ course, isExpanded, onToggle }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header Accordion */}
      <div
        className={`flex justify-between items-center p-5  ${
          isExpanded ? "!pb-3" : ""
        } cursor-pointer ${course.isFinished ? "bg-[#EEEDF4]" : "bg-white"}`}
        onClick={onToggle}
      >
        <div className="flex items-start flex-col">
          {course.user_progress === 100 && (
            <div className="flex items-center bg-purple-100 text-purple-700 text-xs font-bold mr-4 px-3 py-1 rounded-md mb-2">
              <FaCheckCircle className="mr-2" />
              Course Finished
            </div>
          )}
          <div>
            <h3 className="text-xl font-bold !m-0 !mb-[-.25rem] text-black">
              {course.title}
            </h3>
            <p
              className="text-sm !font-normal text-[#585F72]"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(course?.description),
              }}
            />
          </div>
        </div>
        {isExpanded ? (
          <FaChevronUp className="w-full max-w-[16px]" />
        ) : (
          <FaChevronDown className="w-full max-w-[16px]" />
        )}
      </div>

      {/* Body Accordion (Collapsible) */}
      {isExpanded && (
        <div className="px-5 pb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {course?.contents?.length ? (
              course?.contents
                .slice(0, 3)
                .map((lesson) => <CourseCard key={lesson.id} lesson={lesson} />)
            ) : (
              <p className="text-gray-400 italic text-center col-span-3 pb-10 text-sm">
                Lesson not available yet.
              </p>
            )}
          </div>
          {Number(course?.lesson_count) + Number(course?.exams_count) > 3 && (
            <div className="flex justify-end mt-4">
              <Link to={`/learn/subcourse/${course?.id}`}>
                <div className="px-6 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer">
                  Explore Course
                </div>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Komponen utama
const ListCourse = ({ courses, loading }) => {
  // State untuk melacak course mana yang sedang terbuka (expanded)
  // Defaultnya, course pertama terbuka sesuai gambar
  const [expandedCourseId, setExpandedCourseId] = useState(null);

  const handleToggle = (courseId) => {
    // Jika course yang diklik sudah terbuka, tutup. Jika tidak, buka.
    setExpandedCourseId(expandedCourseId === courseId ? null : courseId);
  };

  useEffect(() => {
    if (courses.length && expandedCourseId === null) {
      setExpandedCourseId(courses[0]?.id);
    }
  }, [courses]);

  return (
    <div className="w-full mx-auto my-5">
      <h2 className="text-xl md:text-2xl font-bold mb-6">Courses</h2>
      <div className="flex flex-col gap-4">
        {loading ? (
          [...Array(5)].map((_, idx) => <CourseItemSkeleton key={idx} />)
        ) : courses?.length ? (
          courses.map((course) => (
            <CourseItem
              key={course.id}
              course={course}
              isExpanded={expandedCourseId === course.id}
              onToggle={() => handleToggle(course.id)}
            />
          ))
        ) : (
          <div className="flex justify-center italic items-center h-40 w-full text-sm p-3 text-center text-gray-400 bg-white rounded-xl">
            Sorry we couldn't find any available courses at the moment. <br />{" "}
            Please try again later.
          </div>
        )}
      </div>
    </div>
  );
};

export default ListCourse;
